import { useEffect, useMemo, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    clearDismissedDuplicateAddressCandidates,
    dismissDuplicateAddressCandidate,
    getDuplicateAddressCandidates,
    purgeDeliveriesForHiddenAddress
} from "../api/addresses";
import { useUpdateAddress } from "../api/addresses/hooks";
import type { DuplicateAddressCandidate } from "../api/addresses/types";
import { fetchAllAddressPointsInBbox } from "../api/map/api";
import { mapboxAccessToken, mapboxSatelliteStyle, mapInitialView } from "../map/config";
import { showError, showInfo, showSuccess } from "../shared/toast";
import "./hidden-addresses-panel.css";
import "./duplicate-address-candidates.css";

const QUERY_KEY = ["addresses", "duplicate-candidates"];
const CONTEXT_QUERY_KEY = "duplicate-candidate-context";
const LINES_SOURCE_ID = "duplicate-candidate-lines";
const POINTS_SOURCE_ID = "duplicate-candidate-points";
const POINT_LABELS_LAYER_ID = "duplicate-candidate-point-labels";
const CONTEXT_SOURCE_ID = "duplicate-candidate-context";
const CONTEXT_LABELS_LAYER_ID = "duplicate-candidate-context-labels";

/** Below this zoom the surrounding address points are neither fetched nor drawn. */
const CONTEXT_MIN_ZOOM = 16;
/** Share of the viewport added around the fetched box, so small pans reuse the same fetch. */
const CONTEXT_BBOX_PADDING = 0.25;

const COLOR_SELECTED_PAIR = "#d92b2b";
const COLOR_PAIR = "#f0a500";
const COLOR_CONTEXT = "#2f9bff";
const COLOR_CONTEXT_HIDDEN = "#9aa5b1";

interface ContextBox {
    west: number;
    south: number;
    east: number;
    north: number;
}

function formatAddressLine(street: string, number?: string | null): string {
    return [street, number].filter(Boolean).join(" ");
}

function pairKey(candidate: DuplicateAddressCandidate): string {
    return [candidate.id1, candidate.id2].sort().join(":");
}

interface PostalGroup {
    postalCode: string;
    city: string;
    rows: DuplicateAddressCandidate[];
}

function groupByPostalCode(rows: DuplicateAddressCandidate[]): PostalGroup[] {
    const groups = new Map<string, PostalGroup>();
    for (const row of rows) {
        const key = row.postalCode1 || "Okänt postnummer";
        let group = groups.get(key);
        if (!group) {
            group = { postalCode: key, city: row.city1, rows: [] };
            groups.set(key, group);
        }
        group.rows.push(row);
    }
    for (const group of groups.values()) {
        group.rows.sort((a, b) => {
            const streetCmp = a.street1.localeCompare(b.street1, "sv");
            if (streetCmp !== 0) {
                return streetCmp;
            }
            return (a.number1 ?? "").localeCompare(b.number1 ?? "", "sv", { numeric: true });
        });
    }
    return Array.from(groups.values()).sort((a, b) => a.postalCode.localeCompare(b.postalCode, "sv"));
}

export const DuplicateAddressCandidatesPage = (): JSX.Element => {
    const queryClient = useQueryClient();
    const updateAddressMutation = useUpdateAddress();
    const purgeMutation = useMutation({ mutationFn: purgeDeliveriesForHiddenAddress });
    const dismissMutation = useMutation({
        mutationFn: ({ id1, id2 }: { id1: string; id2: string }) => dismissDuplicateAddressCandidate(id1, id2)
    });
    const clearDismissedMutation = useMutation({ mutationFn: clearDismissedDuplicateAddressCandidates });
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const [isMapReady, setIsMapReady] = useState(false);
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [mergingKey, setMergingKey] = useState<string | null>(null);
    const [dismissingKey, setDismissingKey] = useState<string | null>(null);
    const [resolvedCount, setResolvedCount] = useState(0);
    const [dismissedCount, setDismissedCount] = useState(0);
    const [selectedPostalCode, setSelectedPostalCode] = useState<string>("all");
    const [onlyMine, setOnlyMine] = useState(false);
    const [showContext, setShowContext] = useState(true);
    const [contextBbox, setContextBbox] = useState<string | null>(null);
    const [isContextZoom, setIsContextZoom] = useState(false);
    const lastFitScopeRef = useRef<string | null>(null);
    const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
    const contextBoxRef = useRef<ContextBox | null>(null);
    const contextPopupRef = useRef<mapboxgl.Popup | null>(null);

    // Dismissed pairs are excluded server-side (stored in the DB, not per-browser), so
    // `candidates` here is already the "still active" list.
    const { data: candidates = [], isLoading, error } = useQuery({
        queryKey: QUERY_KEY,
        queryFn: getDuplicateAddressCandidates
    });

    // Every address point in view, not just the candidate pairs — a pair only makes sense
    // next to its neighbours (is this a row of houses with its own numbers, or one house
    // registered twice?). Fetched per viewport, so it only loads when zoomed in on a pair.
    const contextEnabled = showContext && isContextZoom && contextBbox !== null;
    const { data: contextAddresses } = useQuery({
        queryKey: [CONTEXT_QUERY_KEY, contextBbox],
        queryFn: ({ signal }) => fetchAllAddressPointsInBbox(contextBbox!, signal),
        enabled: contextEnabled,
        staleTime: 5 * 60 * 1000,
        placeholderData: (previous) => previous
    });

    // "Bara mina" keeps the pairs where the signed-in user has logged a delivery on at
    // least one of the two addresses — the rest belong to other distributors' areas and
    // can't affect this user's own statistics.
    const myCandidates = useMemo(
        () => candidates.filter((c) => c.hasOwnDeliveries1 || c.hasOwnDeliveries2),
        [candidates]
    );

    const scopedCandidates = onlyMine ? myCandidates : candidates;

    const postalGroups = useMemo(() => groupByPostalCode(scopedCandidates), [scopedCandidates]);

    const filteredCandidates = useMemo(
        () =>
            selectedPostalCode === "all"
                ? scopedCandidates
                : scopedCandidates.filter((c) => (c.postalCode1 || "Okänt postnummer") === selectedPostalCode),
        [scopedCandidates, selectedPostalCode]
    );

    // Switching to "bara mina" can empty the selected postal code entirely; fall back to
    // "all" so the list never looks broken after ticking the box.
    useEffect(() => {
        if (selectedPostalCode !== "all" && !postalGroups.some((g) => g.postalCode === selectedPostalCode)) {
            setSelectedPostalCode("all");
        }
    }, [postalGroups, selectedPostalCode]);

    const displayedGroups = useMemo(() => groupByPostalCode(filteredCandidates), [filteredCandidates]);

    // Only depends on stable mutation/query-client references, so it's safe to capture once
    // inside the map-init effect below (which only runs on mount) without going stale.
    const dismissPair = async (id1: string, id2: string) => {
        const key = [id1, id2].sort().join(":");
        setDismissingKey(key);
        try {
            await dismissMutation.mutateAsync({ id1, id2 });
            setDismissedCount((n) => n + 1);
            showInfo("Markerad som olika adresser");
            await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        } catch (e) {
            const msg = e instanceof Error ? e.message : "Okänt fel";
            showError("Kunde inte markera som olika adresser", msg);
        } finally {
            setDismissingKey(null);
        }
    };

    // Map container is always rendered (not gated behind isLoading) so this init effect,
    // which only runs once on mount, always finds a real DOM node to attach to.
    useEffect(() => {
        if (mapRef.current || !mapContainerRef.current || !mapboxAccessToken) {
            return;
        }

        mapboxgl.accessToken = mapboxAccessToken;
        const map = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: mapboxSatelliteStyle,
            center: mapInitialView.center,
            zoom: mapInitialView.zoom,
            minZoom: 3,
            maxZoom: 19
        });
        map.addControl(new mapboxgl.NavigationControl(), "top-right");

        map.once("load", () => {
            map.addSource(LINES_SOURCE_ID, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
            map.addSource(POINTS_SOURCE_ID, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
            map.addSource(CONTEXT_SOURCE_ID, { type: "geojson", data: { type: "FeatureCollection", features: [] } });

            // Added first so the candidate pair always draws on top of its surroundings.
            map.addLayer({
                id: CONTEXT_SOURCE_ID,
                type: "circle",
                source: CONTEXT_SOURCE_ID,
                paint: {
                    "circle-radius": ["case", ["get", "hidden"], 4, 5],
                    "circle-color": ["case", ["get", "hidden"], COLOR_CONTEXT_HIDDEN, COLOR_CONTEXT],
                    "circle-opacity": ["case", ["get", "hidden"], 0.6, 0.95],
                    "circle-stroke-color": "#ffffff",
                    "circle-stroke-width": 1
                }
            });

            map.addLayer({
                id: LINES_SOURCE_ID,
                type: "line",
                source: LINES_SOURCE_ID,
                paint: {
                    "line-color": ["case", ["get", "selected"], "#d92b2b", "#f0a500"],
                    "line-width": ["case", ["get", "selected"], 4, 2],
                    "line-dasharray": [2, 1]
                }
            });

            map.addLayer({
                id: POINTS_SOURCE_ID,
                type: "circle",
                source: POINTS_SOURCE_ID,
                paint: {
                    "circle-radius": ["case", ["get", "selected"], 8, 6],
                    "circle-color": ["case", ["get", "selected"], COLOR_SELECTED_PAIR, COLOR_PAIR],
                    "circle-stroke-color": "#ffffff",
                    "circle-stroke-width": 2
                }
            });

            // Labels last, so they stay readable on top of every circle.
            map.addLayer({
                id: CONTEXT_LABELS_LAYER_ID,
                type: "symbol",
                source: CONTEXT_SOURCE_ID,
                minzoom: 17,
                layout: {
                    "text-field": ["get", "label"],
                    "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
                    "text-size": 11,
                    "text-offset": [0, 0.9],
                    "text-anchor": "top",
                    "text-optional": true
                },
                paint: {
                    "text-color": "#eaf4ff",
                    "text-halo-color": "#0b2030",
                    "text-halo-width": 1.4
                }
            });

            map.addLayer({
                id: POINT_LABELS_LAYER_ID,
                type: "symbol",
                source: POINTS_SOURCE_ID,
                minzoom: 17,
                layout: {
                    "text-field": ["get", "label"],
                    "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
                    "text-size": 12,
                    "text-offset": [0, -1],
                    "text-anchor": "bottom",
                    "text-allow-overlap": true
                },
                paint: {
                    "text-color": ["case", ["get", "selected"], "#ffdede", "#fff0cc"],
                    "text-halo-color": "#3a1400",
                    "text-halo-width": 1.6
                }
            });

            const selectFromEvent = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
                const key = e.features?.[0]?.properties?.pairKey;
                if (key) {
                    setSelectedKey(key);
                }
            };

            map.on("click", LINES_SOURCE_ID, selectFromEvent);
            map.on("click", POINTS_SOURCE_ID, selectFromEvent);
            map.on("mouseenter", POINTS_SOURCE_ID, () => {
                map.getCanvas().style.cursor = "pointer";
            });
            map.on("mouseleave", POINTS_SOURCE_ID, () => {
                map.getCanvas().style.cursor = "";
            });

            const dismissFromEvent = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
                e.preventDefault();
                const props = e.features?.[0]?.properties;
                if (props?.id1 && props?.id2) {
                    void dismissPair(props.id1, props.id2);
                }
            };

            map.on("contextmenu", LINES_SOURCE_ID, dismissFromEvent);
            map.on("contextmenu", POINTS_SOURCE_ID, dismissFromEvent);

            const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 10 });
            contextPopupRef.current = popup;

            map.on("mousemove", CONTEXT_SOURCE_ID, (e) => {
                const feature = e.features?.[0];
                if (!feature) {
                    return;
                }
                map.getCanvas().style.cursor = "pointer";
                const props = feature.properties ?? {};
                const suffix = props.hidden ? " (dold)" : "";
                popup
                    .setLngLat(e.lngLat)
                    .setText(`${props.label ?? ""}, ${props.postalCode ?? ""} ${props.city ?? ""}${suffix}`.trim())
                    .addTo(map);
            });

            map.on("mouseleave", CONTEXT_SOURCE_ID, () => {
                map.getCanvas().style.cursor = "";
                popup.remove();
            });

            // Surrounding points are viewport-scoped: refetch only when the view leaves the
            // padded box we last loaded, so panning around one pair reuses a single request.
            const syncContextViewport = () => {
                const zoom = map.getZoom();
                setIsContextZoom(zoom >= CONTEXT_MIN_ZOOM);
                if (zoom < CONTEXT_MIN_ZOOM) {
                    return;
                }
                const bounds = map.getBounds();
                if (!bounds) {
                    return;
                }
                const west = bounds.getWest();
                const south = bounds.getSouth();
                const east = bounds.getEast();
                const north = bounds.getNorth();
                const loaded = contextBoxRef.current;
                if (loaded && west >= loaded.west && south >= loaded.south && east <= loaded.east && north <= loaded.north) {
                    return;
                }
                const padLng = (east - west) * CONTEXT_BBOX_PADDING;
                const padLat = (north - south) * CONTEXT_BBOX_PADDING;
                const box: ContextBox = {
                    west: west - padLng,
                    south: south - padLat,
                    east: east + padLng,
                    north: north + padLat
                };
                contextBoxRef.current = box;
                setContextBbox(`${box.west},${box.south},${box.east},${box.north}`);
            };

            map.on("moveend", syncContextViewport);
            syncContextViewport();

            setIsMapReady(true);
        });

        mapRef.current = map;

        return () => {
            contextPopupRef.current?.remove();
            contextPopupRef.current = null;
            map.remove();
            mapRef.current = null;
        };
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !isMapReady) {
            return;
        }

        const lineFeatures: GeoJSON.Feature[] = filteredCandidates.map((c) => ({
            type: "Feature",
            properties: { pairKey: pairKey(c), id1: c.id1, id2: c.id2, selected: pairKey(c) === selectedKey },
            geometry: {
                type: "LineString",
                coordinates: [
                    [c.longitude1, c.latitude1],
                    [c.longitude2, c.latitude2]
                ]
            }
        }));

        const pointFeatures: GeoJSON.Feature[] = filteredCandidates.flatMap((c) => {
            const selected = pairKey(c) === selectedKey;
            const properties = { pairKey: pairKey(c), id1: c.id1, id2: c.id2, selected };
            return [
                {
                    type: "Feature",
                    properties: { ...properties, label: formatAddressLine(c.street1, c.number1) },
                    geometry: { type: "Point", coordinates: [c.longitude1, c.latitude1] }
                },
                {
                    type: "Feature",
                    properties: { ...properties, label: formatAddressLine(c.street2, c.number2) },
                    geometry: { type: "Point", coordinates: [c.longitude2, c.latitude2] }
                }
            ];
        });

        (map.getSource(LINES_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined)?.setData({
            type: "FeatureCollection",
            features: lineFeatures
        });
        (map.getSource(POINTS_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined)?.setData({
            type: "FeatureCollection",
            features: pointFeatures
        });

        const fitScope = `${onlyMine ? "mine" : "all"}:${selectedPostalCode}`;
        if (lastFitScopeRef.current !== fitScope && filteredCandidates.length > 0) {
            const bounds = new mapboxgl.LngLatBounds();
            filteredCandidates.forEach((c) => {
                bounds.extend([c.longitude1, c.latitude1]);
                bounds.extend([c.longitude2, c.latitude2]);
            });
            map.fitBounds(bounds, { padding: 60, maxZoom: 18 });
            lastFitScopeRef.current = fitScope;
        }
    }, [filteredCandidates, selectedKey, isMapReady, selectedPostalCode, onlyMine]);

    // Addresses already drawn as candidate points must not be re-drawn as context points,
    // or the pair colours would be hidden underneath the neutral ones.
    const candidateAddressIds = useMemo(() => {
        const ids = new Set<string>();
        for (const c of filteredCandidates) {
            ids.add(c.id1);
            ids.add(c.id2);
        }
        return ids;
    }, [filteredCandidates]);

    const contextFeatures = useMemo<GeoJSON.Feature[]>(() => {
        if (!contextEnabled || !contextAddresses) {
            return [];
        }
        return contextAddresses.features
            .filter((f) => !candidateAddressIds.has(f.properties.addressPointId))
            .map((f) => ({
                type: "Feature",
                properties: {
                    label: formatAddressLine(f.properties.street, f.properties.number),
                    postalCode: f.properties.postalCode,
                    city: f.properties.city,
                    hidden: f.properties.isHidden
                },
                geometry: { type: "Point", coordinates: f.geometry.coordinates as [number, number] }
            }));
    }, [contextAddresses, contextEnabled, candidateAddressIds]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !isMapReady) {
            return;
        }
        (map.getSource(CONTEXT_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined)?.setData({
            type: "FeatureCollection",
            features: contextFeatures
        });
        if (contextFeatures.length === 0) {
            contextPopupRef.current?.remove();
        }
    }, [contextFeatures, isMapReady]);

    useEffect(() => {
        if (!selectedKey) {
            return;
        }
        rowRefs.current.get(selectedKey)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, [selectedKey]);

    const flyTo = (candidate: DuplicateAddressCandidate) => {
        setSelectedKey(pairKey(candidate));
        const map = mapRef.current;
        if (!map) {
            showError("Kartan är inte redo än, försök igen om en stund");
            return;
        }
        const midLng = (candidate.longitude1 + candidate.longitude2) / 2;
        const midLat = (candidate.latitude1 + candidate.latitude2) / 2;
        map.flyTo({ center: [midLng, midLat], zoom: 19 });
    };

    // Keeps the topmost row in view auto-selected — on first load, after a postal-code
    // filter change, and after "Behåll denna"/"Inte samma" removes the selected pair and
    // the list reflows — so working the list top-to-bottom never needs an extra "Visa" click.
    useEffect(() => {
        if (!isMapReady || filteredCandidates.length === 0) {
            return;
        }
        const stillVisible = selectedKey !== null && filteredCandidates.some((c) => pairKey(c) === selectedKey);
        if (stillVisible) {
            return;
        }
        const top = displayedGroups[0]?.rows[0];
        if (top) {
            flyTo(top);
        }
    }, [displayedGroups, filteredCandidates, selectedKey, isMapReady]);

    const clearAllDismissed = async () => {
        try {
            const { clearedCount } = await clearDismissedMutation.mutateAsync();
            setDismissedCount(0);
            showSuccess(clearedCount > 0 ? `${clearedCount} dolda par visas igen` : "Inga dolda par att visa");
            await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        } catch (e) {
            const msg = e instanceof Error ? e.message : "Okänt fel";
            showError("Kunde inte visa dolda par igen", msg);
        }
    };

    const keepAddress = async (candidate: DuplicateAddressCandidate, which: 1 | 2) => {
        const kept = which === 1
            ? formatAddressLine(candidate.street1, candidate.number1)
            : formatAddressLine(candidate.street2, candidate.number2);
        const droppedId = which === 1 ? candidate.id2 : candidate.id1;
        const dropped = which === 1
            ? formatAddressLine(candidate.street2, candidate.number2)
            : formatAddressLine(candidate.street1, candidate.number1);

        const confirmed = window.confirm(
            `Dölj ${dropped} (${candidate.distanceMeters} m bort) och ta bort dess utdelningar? ` +
                `${kept} lämnas orörd.`
        );
        if (!confirmed) {
            return;
        }

        const key = pairKey(candidate);
        setMergingKey(key);
        try {
            await updateAddressMutation.mutateAsync({ id: droppedId, request: { isHidden: true } });
            const { deletedCount } = await purgeMutation.mutateAsync(droppedId);
            showSuccess(
                deletedCount > 0
                    ? `Dolde ${dropped} och tog bort ${deletedCount} utdelning${deletedCount === 1 ? "" : "ar"}`
                    : `Dolde ${dropped}`
            );
            setResolvedCount((n) => n + 1);
            await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
            // The dropped address is now hidden — refetch so it redraws as a "dold adress".
            await queryClient.invalidateQueries({ queryKey: [CONTEXT_QUERY_KEY] });
        } catch (e) {
            const msg = e instanceof Error ? e.message : "Okänt fel";
            showError(`Kunde inte dölja ${dropped}`, msg);
        } finally {
            setMergingKey(null);
        }
    };

    if (error) {
        return (
            <div className="duplicate-candidates-page">
                <h2>Möjliga adressdubbletter</h2>
                <p className="error-message">Kunde inte ladda kandidater.</p>
            </div>
        );
    }

    return (
        <section className="duplicate-candidates-page" aria-label="Möjliga adressdubbletter">
            <div className="duplicate-candidates-page__header">
                <h2>Möjliga adressdubbletter</h2>
                <p className="duplicate-candidates-page__description">
                    Adresspar nära varandra på olika gator — t.ex. samma hus registrerat med adress mot två
                    gator i en korsning. Detta är en gissning baserad på avstånd, inte en bekräftad lista:
                    bedöm varje par på kartan innan du slår ihop dem.
                </p>
                <div className="duplicate-candidates-page__stats">
                    {isLoading ? (
                        "Laddar..."
                    ) : (
                        <>
                            <strong>{filteredCandidates.length}</strong> kvar
                            {onlyMine && <> med mina utdelningar</>}
                            {selectedPostalCode !== "all" && <> i {selectedPostalCode}</>} (av {candidates.length} laddade)
                            {resolvedCount > 0 && <> · {resolvedCount} lösta</>}
                            {dismissedCount > 0 && <> · {dismissedCount} markerade som olika</>}
                            {" "}
                            <button className="action-button" onClick={clearAllDismissed}>
                                Visa dolda par igen
                            </button>
                            <label className="duplicate-candidates-page__only-mine">
                                <input
                                    type="checkbox"
                                    checked={onlyMine}
                                    onChange={(e) => setOnlyMine(e.target.checked)}
                                />
                                Bara mina ({myCandidates.length})
                            </label>
                            <select
                                className="duplicate-candidates-page__postal-select"
                                value={selectedPostalCode}
                                onChange={(e) => setSelectedPostalCode(e.target.value)}
                            >
                                <option value="all">Alla postnummer ({scopedCandidates.length})</option>
                                {postalGroups.map((g) => (
                                    <option key={g.postalCode} value={g.postalCode}>
                                        {g.postalCode} {g.city} ({g.rows.length})
                                    </option>
                                ))}
                            </select>
                        </>
                    )}
                </div>
                <div className="duplicate-candidates-page__legend">
                    <label className="duplicate-candidates-page__legend-toggle">
                        <input
                            type="checkbox"
                            checked={showContext}
                            onChange={(e) => setShowContext(e.target.checked)}
                        />
                        Visa övriga adresspunkter
                    </label>
                    <span className="duplicate-candidates-page__legend-item">
                        <i style={{ background: COLOR_SELECTED_PAIR }} />Valt par
                    </span>
                    <span className="duplicate-candidates-page__legend-item">
                        <i style={{ background: COLOR_PAIR }} />Övriga par
                    </span>
                    <span className="duplicate-candidates-page__legend-item">
                        <i style={{ background: COLOR_CONTEXT }} />Övrig adress
                    </span>
                    <span className="duplicate-candidates-page__legend-item">
                        <i style={{ background: COLOR_CONTEXT_HIDDEN }} />Dold adress
                    </span>
                    {showContext && (
                        <span className="duplicate-candidates-page__legend-hint">
                            {isContextZoom
                                ? `${contextFeatures.length} övriga adresspunkter i vyn`
                                : "Zooma in för att visa övriga adresspunkter"}
                        </span>
                    )}
                </div>
            </div>

            <div className="duplicate-candidates-page__layout">
                <div className="duplicate-candidates-page__map" ref={mapContainerRef} />
                <div className="duplicate-candidates-page__list">
                    {!isLoading && filteredCandidates.length === 0 ? (
                        <p className="hidden-addresses-panel__empty">
                            {candidates.length === 0
                                ? "Inga kandidater hittades."
                                : onlyMine && scopedCandidates.length === 0
                                  ? "Inga kandidater på adresser du delat ut till."
                                  : "Inga kandidater kvar i det här urvalet."}
                        </p>
                    ) : (
                        displayedGroups.map((group) => (
                            <div className="hidden-addresses-panel__group" key={group.postalCode}>
                                <div className="hidden-addresses-panel__group-header">
                                    <strong>{group.postalCode}</strong> {group.city}
                                    <span className="hidden-addresses-panel__group-meta">{group.rows.length} par</span>
                                </div>
                                <table className="hidden-addresses-table">
                                    <thead>
                                        <tr>
                                            <th>Karta</th>
                                            <th>Adress 1</th>
                                            <th>Adress 2</th>
                                            <th>Avstånd</th>
                                            <th>Olika?</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {group.rows.map((c) => {
                                            const key = pairKey(c);
                                            const isMerging = mergingKey === key;
                                            const isDismissing = dismissingKey === key;
                                            const isBusy = isMerging || isDismissing;
                                            return (
                                                <tr
                                                    key={key}
                                                    ref={(el) => {
                                                        if (el) {
                                                            rowRefs.current.set(key, el);
                                                        } else {
                                                            rowRefs.current.delete(key);
                                                        }
                                                    }}
                                                    className={selectedKey === key ? "duplicate-candidates-page__row--selected" : undefined}
                                                >
                                                    <td>
                                                        <button className="action-button" onClick={() => flyTo(c)} disabled={isBusy}>
                                                            Visa
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <div>
                                                            {formatAddressLine(c.street1, c.number1)}, {c.city1}
                                                        </div>
                                                        <button
                                                            className="action-button action-button--approve"
                                                            onClick={() => keepAddress(c, 1)}
                                                            disabled={isBusy}
                                                        >
                                                            {isMerging ? "..." : "Behåll denna"}
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <div>
                                                            {formatAddressLine(c.street2, c.number2)}, {c.city2}
                                                        </div>
                                                        <button
                                                            className="action-button action-button--approve"
                                                            onClick={() => keepAddress(c, 2)}
                                                            disabled={isBusy}
                                                        >
                                                            {isMerging ? "..." : "Behåll denna"}
                                                        </button>
                                                    </td>
                                                    <td>{c.distanceMeters} m</td>
                                                    <td>
                                                        <button
                                                            className="action-button"
                                                            onClick={() => dismissPair(c.id1, c.id2)}
                                                            disabled={isBusy}
                                                        >
                                                            {isDismissing ? "..." : "Inte samma"}
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </section>
    );
};
